/**
 * SA-1329: the presentational pieces of a triage block, props only. The panel
 * owns every decision; these draw a message's header, its quote, the AI's
 * proposal, what approving would do and why.
 */
import type { ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge, Button, Tip } from '@/components/ui'
import type { TriageKindOption, TriageRow } from '@/types/inbox'
import type { NeedsChoice, ReceiptChip } from './triageBlocks'
import {
  aiMark, closestText, consequenceParts, FOLD_TIP, NEEDS_A_KIND, needsChoiceText, NOT_AN_ENQUIRY, openRecordText, recordBandLine,
  reinstateText, SHOW_IN_THREAD, WHY_LEAD, WORKING_LABEL,
} from './triageText'

interface BlockHeaderProps {
  /** Task 8's selection checkbox. */
  select?: ReactNode
  sender: string
  when: string | null
  context: string | null
  onShow?: () => void
}

export function BlockHeader({ select, sender, when, context, onShow }: BlockHeaderProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      {select}
      <span className="text-sm font-semibold text-foreground [overflow-wrap:anywhere]">{sender}</span>
      {when && <span>{when}</span>}
      {context && <span>· {context}</span>}
      {onShow && (
        <Button size="sm" variant="ghost" className="ml-auto h-6 px-1.5 text-xs" onClick={onShow}>
          {SHOW_IN_THREAD}
        </Button>
      )}
    </div>
  )
}

export function BlockQuote({ lines, clamp = false, foot, testId }: { lines: string[]; clamp?: boolean; foot: string | null; testId?: string }) {
  const shown = lines.filter((line) => line.trim() !== '')
  if (shown.length === 0 && !foot) return null
  return (
    <div className="space-y-0.5 text-xs text-muted-foreground">
      {shown.length > 0 && (
        <div data-testid={testId} className="border-l-2 border-border pl-3">
          {shown.map((line, i) => (
            <p key={i} className={clamp ? 'line-clamp-2 [overflow-wrap:anywhere]' : 'truncate'}>{line}</p>
          ))}
        </div>
      )}
      {foot && <p data-testid="email-row-files" className="truncate">{foot}</p>}
    </div>
  )
}

interface ProposalLineProps {
  row: TriageRow
  option: TriageKindOption | undefined
  choice: NeedsChoice
  noun: string
  /** A decided row names the kind it was given; the AI's mark stays only on a decision it made. */
  decided?: boolean
}

export function ProposalLine({ row, option, choice, noun, decided = false }: ProposalLineProps) {
  const unknown = row.kind === 'unknown'
  const label = decided
    ? (row.kindLabel ?? row.kind)
    : unknown ? (row.proposedLabel ?? option?.label ?? null) : (row.proposedLabel ?? option?.label ?? row.kindLabel)
  const mark = decided && row.decisionStatus !== 'auto' ? null : aiMark(row.proposedHow)
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      {unknown && !decided ? (
        <>
          <Badge variant="neutral">{NEEDS_A_KIND}</Badge>
          {label && <span className="text-muted-foreground">{closestText(label)}</span>}
        </>
      ) : (
        label && <Badge variant="info-soft">{label}</Badge>
      )}
      {mark && <Badge variant="soft" className="bg-role-ai/12 text-role-ai">{mark}</Badge>}
      {choice && <span className="text-warning-strong">{needsChoiceText(choice, noun)}</span>}
    </p>
  )
}

export function Consequence({ parts }: { parts: ReturnType<typeof consequenceParts> }) {
  if (!parts) return null
  return (
    <p className="text-sm text-foreground">
      {parts.lead}<span className="font-semibold">{parts.phrase}</span>{parts.end}
    </p>
  )
}

export function WhyLine({ rationale }: { rationale: string }) {
  return (
    <p className="text-xs text-muted-foreground">
      {WHY_LEAD}{'"'}<em>{rationale}</em>{'"'}
    </p>
  )
}

interface ReceiptLineProps {
  chip: ReceiptChip
  sender: string
  when: string | null
  words: string
  open: boolean
  /** Absent when the line never folds: it is a plain row, with no chevron. */
  onToggle?: () => void
  onShow?: () => void
  /** The selection checkbox of a block that can be selected. */
  select?: ReactNode
}

/** A decided or failed message as one line: its status first, then who, when and how it was decided. */
export function ReceiptLine({ chip, sender, when, words, open, onToggle, onShow, select }: ReceiptLineProps) {
  // Inside the fold button the chip is plain text: the failure words ride the button's tip and description.
  const rest = (
    <>
      <span className="text-sm font-semibold text-foreground [overflow-wrap:anywhere]">{sender}</span>
      {when && <span className="text-xs text-muted-foreground">{when}</span>}
      <span className="text-xs text-muted-foreground">· {words}</span>
    </>
  )
  const foldTip = open ? FOLD_TIP.hide : FOLD_TIP.show
  const plain = chip.tip ? <Tip text={chip.tip}><Badge variant={chip.variant} tabIndex={0}>{chip.label}</Badge></Tip> : null
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {select}
      {onToggle ? (
        <Tip text={chip.tip ? `${chip.tip} · ${foldTip}` : foldTip}>
          <button type="button" aria-expanded={open} onClick={onToggle} aria-description={chip.tip ?? undefined}
            className="-mx-1 inline-flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-sm px-1 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {open ? <ChevronDown className="size-3 shrink-0" aria-hidden="true" /> : <ChevronRight className="size-3 shrink-0" aria-hidden="true" />}
            <Badge variant={chip.variant}>{chip.label}</Badge>
            {rest}
          </button>
        </Tip>
      ) : (
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {plain ?? <Badge variant={chip.variant}>{chip.label}</Badge>}
          {rest}
        </div>
      )}
      {onShow && (
        <Button size="sm" variant="ghost" className="ml-auto h-6 px-1.5 text-xs" onClick={onShow}>
          {SHOW_IN_THREAD}
        </Button>
      )}
    </div>
  )
}

interface RecordBandProps {
  lead: string
  number: string | null
  state: string | null
  openHref: string | null
  noun: string
  /** Absent when the viewer may not reject, or the record cannot be. */
  onReject?: () => void
  onReinstate?: () => void
  busy: boolean
  working: 'reject' | 'reinstate' | null
}

/** The record a message made or is about, at the foot of its block. */
export function RecordBand({ lead, number, state, openHref, noun, onReject, onReinstate, busy, working }: RecordBandProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-2 text-xs">
      <span className="text-muted-foreground">
        {recordBandLine(lead, number, noun, state)}
      </span>
      <span className="ml-auto flex flex-wrap items-center gap-3">
        {openHref && (
          <Link to={openHref} className="font-medium text-primary underline-offset-2 hover:underline">
            {openRecordText(noun, number)}
          </Link>
        )}
        {onReject && (
          <Button size="sm" variant="secondary" disabled={busy} isLoading={working === 'reject'} onClick={onReject}
            className="text-xs">
            {working === 'reject' ? WORKING_LABEL.reject : NOT_AN_ENQUIRY}
          </Button>
        )}
        {onReinstate && (
          <Button size="sm" variant="secondary" disabled={busy} isLoading={working === 'reinstate'} onClick={onReinstate}
            className="text-xs">
            {working === 'reinstate' ? WORKING_LABEL.reinstate : reinstateText(noun, number)}
          </Button>
        )}
      </span>
    </div>
  )
}
